import React, { useState, useMemo, useRef, useEffect } from 'react'

import {
  CompanyId,
  DeleteTeams$Request,
  DivisionId,
  DivisionRow,
  GetDivisions$Request,
  RenameTeam$Request,
  UserCompanyDto,
  UserDto,
  AssignTeamsRandomly$Request,
  GenerateTeams$Request,
  DivisionCategoryRow,
  DivisionCategoryType,
  DivisionCategoryId,
  GetDivisionCategories$Request,
  CreateDivisionCategory$Request,
  DeleteDivisionCategory$Request,
  DivisionDto,
} from '../../services/api.types'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import { Card } from '../../components/Card/Card'
import { Box, Grid, IconButton } from '@material-ui/core'
import { t } from 'ttag'
import YumSmallSelect from '../../components/YumSmallSelect/YumSmallSelect'
import YumHeader from '../../components/YumHeader/YumHeader'
import YumMenu, { YumMenuItem } from '../../components/YumMenu/YumMenu'

import { type MRT_ColumnDef, MRT_RowSelectionState } from 'material-react-table'
import {
  getShortDateOnlyWithTimeString,
  getShortDateWithTimeString,
  getShortTimeOnlyWithTimeString,
} from '../../common/helpers/date'
import YumButton from '../../components/YumButton/YumButton'
import { darkBlue, darkerGray, darkGray, gray, red } from '../../styles/material-ui-theme-v2'
import EditIcon from '@material-ui/icons/Edit'
import CreateTeamWrapper from './components/CreateTeam/CreateTeamWrapper'
import { isRtl } from '../../i18nInit'
import DivisionCategorySettingsWrapper from './components/TeamSettings/DivisionCategorySettingsWrapper'
import TeamMembersTableWrapper from './components/TeamMembersTable/TeamMembersTableWrapper'
import TeamStatus from './components/TeamStatus/TeamStatus'
import YumTable from '../../components/YumTable/YumTable'
import YumDialog from '../../components/YumDialog/YumDialog'
import YumDialogTitle from '../../components/YumDialogTitle/YumDialogTitle'
import YumDialogContent from '../../components/YumDialogContent/YumDialogContent'
import YumDialogContentText from '../../components/YumDialogContentText/YumDialogContentText'
import YumDialogActions from '../../components/YumDialogActions/YumDialogActions'
import YumCancelButton from '../../components/YumCancelButton/YumCancelButton'
import YumSubmitButton from '../../components/YumSubmitButton/YumSubmitButton'
import YumSwitch from '../../components/YumSwitch/YumSwitch'
import YumInput from '../../components/YumInput/YumInput'
import { formatThousands } from '../../common/helpers/translations'
import { hasFeatureFlag } from '../../common/helpers/featureFlags'

import { ReactComponent as LockIcon } from '../../assets/v3/icons/lock.svg'
import { ReactComponent as UnlockIcon } from '../../assets/v3/icons/unlock.svg'
import { ReactComponent as GroupInactiveIcon } from '../../assets/v3/icons/group-inactive.svg'
import { ReactComponent as MoreIcon } from '../../assets/v3/icons/more.svg'
import { selectUserCompany } from '../Dashboard/Dashboard'
import GenerateTeamsWrapper from './components/GenerateTeams/GenerateTeamsWrapper'
import AssignTeamsRandomlyWrapper from './components/AssignTeamsRandomly/AssignTeamsRandomlyWrapper'
import DeleteTeamsWrapper from './components/DeleteTeams/DeleteTeamsWrapper'
import DivisionCategoryTypeSelect from '../../components/DivisionCategoryTypeSelect'

// Helper function to get translated division category type name
export const getDivisionCategoryTypeTranslation = (
  type: DivisionCategoryType,
  usePlural: boolean = false,
  onlyEnglish: boolean = false,
): string => {
  switch (type) {
    case DivisionCategoryType.AREA:
      return onlyEnglish ? (usePlural ? `Areas` : `Area`) : usePlural ? t`Areas` : t`Area`
    case DivisionCategoryType.CLUB:
      return onlyEnglish ? (usePlural ? `Clubs` : `Club`) : usePlural ? t`Clubs` : t`Club`
    case DivisionCategoryType.DEPARTMENT:
      return onlyEnglish ? (usePlural ? `Departments` : `Department`) : usePlural ? t`Departments` : t`Department`
    case DivisionCategoryType.DIVISION:
      return onlyEnglish ? (usePlural ? `Divisions` : `Division`) : usePlural ? t`Divisions` : t`Division`
    case DivisionCategoryType.LOCATION:
      return onlyEnglish ? (usePlural ? `Locations` : `Location`) : usePlural ? t`Locations` : t`Location`
    case DivisionCategoryType.REGION:
      return onlyEnglish ? (usePlural ? `Regions` : `Region`) : usePlural ? t`Regions` : t`Region`
    case DivisionCategoryType.STATE:
      return onlyEnglish ? (usePlural ? `States` : `State`) : usePlural ? t`States` : t`State`
    case DivisionCategoryType.CUSTOM:
      return onlyEnglish ? (usePlural ? `Custom` : `Custom`) : usePlural ? t`Custom` : t`Custom`
    default:
      return type
  }
}

// Helper function to translate DivisionCategory objects
export const translateDivisionCategoryType = (
  category: DivisionCategoryRow,
  usePlural: boolean = false,
  onlyEnglish: boolean = false,
): string => {
  // If it's a custom category, return the custom name
  if (category.type === DivisionCategoryType.CUSTOM) {
    return category.customName || category.type
  }

  // For non-custom categories, return the translated type
  return getDivisionCategoryTypeTranslation(category.type, usePlural, onlyEnglish)
}

export interface ITeamManagementDispatchProps {
  //getCompanyTeams: (params: GetCompanyTeams$Request) => void
  getDivisions: (params: GetDivisions$Request) => void
  renameTeam: (params: RenameTeam$Request & { companyId: CompanyId; categoryId?: DivisionCategoryId }) => void
  deleteTeams: (params: DeleteTeams$Request) => void
  assignTeamsRandomly: (params: AssignTeamsRandomly$Request) => void
  lockTeams(payload: DeleteTeams$Request): void
  unlockTeams(payload: DeleteTeams$Request): void
  generateTeams: (params: GenerateTeams$Request) => void
  getCompanyUsers: (params: { companyId: CompanyId }) => void
  setSelectedCompanyId(payload: CompanyId): void
  setSelectedGroupId(payload: DivisionCategoryId | null): void
  setSelectedGroupIdForCompany(payload: { companyId: CompanyId; groupId: DivisionCategoryId | null }): void
  getDivisionCategories: () => void
  createDivisionCategory: (params: CreateDivisionCategory$Request) => void
  deleteDivisionCategory: (params: DeleteDivisionCategory$Request) => void
  clearNotification(): void
  setDivisionsLoading(): void
}

export interface IUserAdminProps extends ITeamManagementDispatchProps {
  //companyTeams: DivisionRow[]
  teamsLoading: boolean
  selectedCompanyId: CompanyId | null
  createdDivisionId: DivisionId | null
  deletedDivisionIds: DivisionId[]
  lastTeamsUpdate: number | null
  lastDivisionSet: number | null
  lastUpdatedTeamSettings: number | null
  managedCompanies: UserCompanyDto[]
  userCompanies: UserCompanyDto[]
  companyUsers: UserDto[]
  usersLoading: boolean
  renameTeamFailed: boolean
  divisionCategories: DivisionCategoryRow[] | undefined
  groupsLoading: boolean
  selectedGroupId: DivisionCategoryId | null
  selectedGroupIdPerCompany: { [companyId: string]: DivisionCategoryId | null }
  //companyGroups: { [companyId: string]: { [categoryId: string]: DivisionRow[] } }
  divisions: DivisionDto[]
}

export default function TeamManagement(props: IUserAdminProps) {
  const {
    teamsLoading,
    selectedCompanyId,
    createdDivisionId,
    deletedDivisionIds,
    lastTeamsUpdate,
    lastDivisionSet,
    lastUpdatedTeamSettings,
    managedCompanies,
    userCompanies,
    companyUsers,
    usersLoading,
    renameTeamFailed,
    divisionCategories,
    groupsLoading,
    selectedGroupId,
    selectedGroupIdPerCompany,
    //companyGroups,
    divisions,
    setSelectedGroupId,
    setSelectedGroupIdForCompany,

    //getCompanyTeams,
    getDivisions,
    renameTeam,
    deleteTeams,
    assignTeamsRandomly,
    lockTeams,
    unlockTeams,
    generateTeams,
    getCompanyUsers,
    setSelectedCompanyId,
    getDivisionCategories,
    createDivisionCategory,
    deleteDivisionCategory,
    clearNotification,
    setDivisionsLoading,
  } = props

  const [userCompany, setUserCompany] = useState(selectUserCompany(managedCompanies, selectedCompanyId))
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
  const [showAssignTeamsDialog, setShowAssignTeamsDialog] = useState(false)
  const [showGenerateTeamsDialog, setShowGenerateTeamsDialog] = useState(false)
  const [showDeleteTeamsDialog, setShowDeleteTeamsDialog] = useState(false)
  const [teamsToGenerate, setTeamsToGenerate] = useState<string>('')
  const [teamsToDelete, setTeamsToDelete] = useState<DivisionId[]>([])
  const selectedTable = useRef<any>(null)
  const [showRenameDialog, setShowRenameDialog] = useState(false)
  const [teamToRename, setTeamToRename] = useState<{ id: DivisionId; name: string } | null>(null)
  const [newTeamName, setNewTeamName] = useState('')
  const [renameLoading, setRenameLoading] = useState(false)

  // Group filter state
  const previousSelectedGroupIdRef = useRef<DivisionCategoryId | null>(null)
  const [showCreateGroupDialog, setShowCreateGroupDialog] = useState(false)
  const [newGroupType, setNewGroupType] = useState<DivisionCategoryType>(DivisionCategoryType.AREA)
  const [newGroupName, setNewGroupName] = useState('')
  const [createGroupLoading, setCreateGroupLoading] = useState(false)

  // Store previous values for comparison
  const prevLastTeamsUpdateRef = useRef(lastTeamsUpdate)
  const prevRenameTeamFailedRef = useRef(renameTeamFailed)

  // Get the selected group for the current company
  const currentSelectedGroupId = useMemo(() => {
    if (!userCompany?.companyId || !selectedGroupIdPerCompany) return null
    return selectedGroupIdPerCompany[userCompany.companyId] || null
  }, [selectedGroupIdPerCompany, userCompany?.companyId])

  // Filter division categories for the current company
  const currentCompanyCategories = useMemo(() => {
    if (!divisionCategories || !userCompany?.companyId) return []
    return divisionCategories.filter((category) => category.companyId === userCompany.companyId)
  }, [divisionCategories, userCompany?.companyId])

  // Resolve the size limit shown in the table: category-specific maxSize when a custom group is
  // selected, otherwise the company's maxTeamSize (used for the Teams view).
  const effectiveMaxSize = useMemo(() => {
    if (currentSelectedGroupId) {
      const selectedCategory = currentCompanyCategories.find((c) => c.id === currentSelectedGroupId)
      return selectedCategory?.maxSize ?? null
    }
    return userCompany?.maxTeamSize ?? null
  }, [currentSelectedGroupId, currentCompanyCategories, userCompany?.maxTeamSize])

  // Get used category types (excluding CUSTOM types)
  const usedCategoryTypes = useMemo(() => {
    if (!currentCompanyCategories) return new Set<DivisionCategoryType>()

    const usedTypes = new Set<DivisionCategoryType>()
    currentCompanyCategories.forEach((category) => {
      // Only track non-CUSTOM types as they can be reused
      if (category.type !== DivisionCategoryType.CUSTOM) {
        usedTypes.add(category.type)
      }
    })
    return usedTypes
  }, [currentCompanyCategories])

  // Reset to a valid type if current selection becomes disabled
  React.useEffect(() => {
    if (usedCategoryTypes.has(newGroupType) && newGroupType !== DivisionCategoryType.CUSTOM) {
      // Find the first available non-CUSTOM type
      const availableTypes = [
        DivisionCategoryType.AREA,
        DivisionCategoryType.CLUB,
        DivisionCategoryType.DEPARTMENT,
        DivisionCategoryType.DIVISION,
        DivisionCategoryType.LOCATION,
        DivisionCategoryType.REGION,
        DivisionCategoryType.STATE,
      ]
      const firstAvailable = availableTypes.find((type) => !usedCategoryTypes.has(type))
      if (firstAvailable) {
        setNewGroupType(firstAvailable)
      } else {
        // If all predefined types are used, default to CUSTOM
        setNewGroupType(DivisionCategoryType.CUSTOM)
      }
    }
  }, [usedCategoryTypes, newGroupType])

  // Add anchor element state for the dropdown menu
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const openMenu = Boolean(menuAnchorEl)

  // Feature flags
  const showGenerateTeamsFeature = hasFeatureFlag(userCompanies, 'generateTeams')
  const showAssignRandomTeamsFeature = hasFeatureFlag(userCompanies, 'assignRandomTeams')

  // Handle menu open and close
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchorEl(null)
  }

  // Modified handlers that also close the menu
  const handleGenerateTeamsFromMenu = () => {
    handleMenuClose()
    handleGenerateTeamsClick()
  }

  const handleAssignTeamsRandomlyFromMenu = () => {
    handleMenuClose()
    handleAssignTeamsRandomly()
  }

  // Create menu items array for the YumMenu component
  const menuItems: YumMenuItem[] = useMemo(() => {
    const items: YumMenuItem[] = []

    if (showGenerateTeamsFeature) {
      items.push({
        id: 'generate-teams',
        label: t`Generate teams`,
        onClick: handleGenerateTeamsFromMenu,
      })
    }

    if (showAssignRandomTeamsFeature) {
      items.push({
        id: 'assign-random-teams',
        label: t`Assign random teams`,
        onClick: handleAssignTeamsRandomlyFromMenu,
      })
    }

    return items
  }, [
    showGenerateTeamsFeature,
    showAssignRandomTeamsFeature,
    handleGenerateTeamsFromMenu,
    handleAssignTeamsRandomlyFromMenu,
  ])

  // const loadCompanyTeams = () => {
  //   const { companyId } = userCompany
  //   getCompanyTeams({ companyId })
  // }

  const loadDivisions = () => {
    const { companyId } = userCompany
    getDivisions({ companyId })
  }

  const loadDivisionCategories = () => {
    getDivisionCategories({ companyId: userCompany.companyId })
  }

  const resetRowSelection = () => {
    setRowSelection({})
  }

  // TODO: This causes weird bug!!
  // Load company teams if any user's team has changed
  /*React.useEffect(() => {
    loadDivisions()
  }, [lastDivisionSet, currentSelectedGroupId, userCompany.companyId])*/

  // Handle successful rename based on lastTeamsUpdate change
  React.useEffect(() => {
    // Check if lastTeamsUpdate has changed and if there was a rename operation in progress
    if (renameLoading && lastTeamsUpdate !== prevLastTeamsUpdateRef.current) {
      setRenameLoading(false)
      setShowRenameDialog(false)
      setTeamToRename(null)
    }

    // Update the ref with current value for next comparison
    prevLastTeamsUpdateRef.current = lastTeamsUpdate
  }, [lastTeamsUpdate, renameLoading])

  // Handle failed rename
  /*React.useEffect(() => {
    // Check if renameTeamFailed changed to true during a loading state
    if (renameLoading && renameTeamFailed && renameTeamFailed !== prevRenameTeamFailedRef.current) {
      setRenameLoading(false)
      setShowRenameDialog(false)
      setTeamToRename(null)
    }

    // Update previous value ref
    prevRenameTeamFailedRef.current = renameTeamFailed
  }, [renameTeamFailed, renameLoading])*/

  React.useEffect(() => {
    setDivisionsLoading()
  }, [userCompany?.companyId])

  // Load initial data when component mounts or company changes
  React.useEffect(() => {
    //setDivisionsLoading()
    loadDivisionCategories()
    // For TeamManagement, we want to get users with their division assignments
    // The divisionIds parameter will be handled by the backend to include division assignments
    getCompanyUsers({ companyId: userCompany.companyId })
    getDivisions({ companyId: userCompany.companyId })
    resetRowSelection()
  }, [createdDivisionId, deletedDivisionIds, lastTeamsUpdate, lastUpdatedTeamSettings, userCompany?.companyId])

  React.useEffect(() => {
    setUserCompany(managedCompanies.find((company) => company.companyId === userCompany.companyId) as UserCompanyDto)
  }, [managedCompanies])

  const handleCompanyChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const { value } = event.target as { value: CompanyId }
    setUserCompany(managedCompanies.find((company) => company.companyId === value) as UserCompanyDto)

    setSelectedCompanyId(value)
  }

  const handleRenameTeam = (divisionId: DivisionId, currentName: string) => (event: any) => {
    event.stopPropagation()
    setTeamToRename({ id: divisionId, name: currentName })
    setNewTeamName(currentName)
    setShowRenameDialog(true)
  }

  const closeRenameDialog = () => {
    setShowRenameDialog(false)
    setTeamToRename(null)
  }

  const handleTeamNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewTeamName(e.target.value)
  }

  const submitRenameTeam = () => {
    if (!teamToRename || !newTeamName.trim() || renameLoading || newTeamName.trim() === teamToRename.name) return
    setRenameLoading(true)
    renameTeam({
      divisionId: teamToRename.id,
      name: newTeamName.trim(),
      companyId: userCompany.companyId,
      categoryId: currentSelectedGroupId || undefined,
    })
    // Dialog will be closed when lastTeamsUpdate changes in the useEffect
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && newTeamName.trim()) {
      submitRenameTeam()
    }
  }

  const handleDeleteTeams = (table: any) => () => {
    selectedTable.current = table
    const divisionIds = table.getSelectedRowModel().flatRows.map(({ original: { id } }: any) => id)
    setTeamsToDelete(divisionIds)
    setShowDeleteTeamsDialog(true)
  }

  const closeDeleteTeamsDialog = () => {
    setShowDeleteTeamsDialog(false)
    setTeamsToDelete([])

    // Reset selection after closing the dialog
    if (selectedTable.current) {
      selectedTable.current.resetRowSelection()
    }
  }

  const handleAssignTeamsRandomly = () => {
    setShowAssignTeamsDialog(true)
  }

  const closeAssignTeamsDialog = () => {
    setShowAssignTeamsDialog(false)
  }

  const handleGenerateTeamsClick = () => {
    setShowGenerateTeamsDialog(true)
  }

  const closeGenerateTeamsDialog = () => {
    setShowGenerateTeamsDialog(false)
    setTeamsToGenerate('')
  }

  const handleTeamsToGenerateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    const numValue = parseInt(value, 10)

    if (value === '') {
      setTeamsToGenerate('')
    } else if (!isNaN(numValue)) {
      if (numValue > 1000) {
        setTeamsToGenerate('1000')
      } else {
        setTeamsToGenerate(numValue.toString())
      }
    }
  }

  const isValidInput = () => {
    const numValue = parseInt(teamsToGenerate, 10)
    return !isNaN(numValue) && numValue > 0 && numValue <= 1000
  }

  const submitGenerateTeams = () => {
    if (!isValidInput()) return

    const numTeams = parseInt(teamsToGenerate, 10)

    generateTeams({
      companyId: userCompany.companyId,
      amount: numTeams,
    })
    closeGenerateTeamsDialog()
  }

  const handleLockTeams = (table: any) => async () => {
    const divisionIds = table.getSelectedRowModel().flatRows.map(({ original: { id } }: any) => id)
    lockTeams({ divisionIds })
  }

  const handleUnlockTeams = (table: any) => async () => {
    const divisionIds = table.getSelectedRowModel().flatRows.map(({ original: { id } }: any) => id)
    unlockTeams({ divisionIds })
  }

  // Filter teams based on selected group
  const filteredTeams = useMemo(() => {
    if (!userCompany?.companyId) return []
    const categoryId = currentSelectedGroupId || null
    return divisions.filter((d) => d.companyId === userCompany.companyId && d.categoryId === categoryId) || {}
  }, [currentSelectedGroupId, divisions, userCompany?.companyId])

  const columns = useMemo<MRT_ColumnDef<DivisionRow>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t`Name`,
        sortingFn: 'alphanumeric',
        Cell: ({
          row: {
            original: { id, name },
          },
        }: any) => (
          <>
            {name}
            <IconButton
              edge="end"
              style={{ padding: 3, marginLeft: 3, marginTop: -1 }}
              onClick={handleRenameTeam(id, name)}
              title={t`Rename team`}
              aria-label={t`Rename team`}
            >
              <EditIcon style={{ color: darkGray, width: '0.75em', height: '0.75em' }} aria-hidden="true" />
            </IconButton>
          </>
        ),
      },
      {
        accessorKey: 'members',
        header: t`Members`,
        Cell: ({
          row: {
            original: { members },
          },
        }: any) => {
          let x = members
          if (effectiveMaxSize) {
            x = `${members}/${effectiveMaxSize}`
            return t`${x} users`
          }
          return members == 1 ? t`1 user` : t`${x} users`
        },
      },
      {
        accessorKey: 'locked',
        header: t`Status`,
        Cell: ({ row: { original: team } }: any) => <TeamStatus team={team} maxTeamSize={effectiveMaxSize} />,
      },
      {
        accessorKey: 'createdAt',
        header: t`Created`,
        // Convert to Date for sorting and filtering
        accessorFn: (row) => new Date(row.createdAt),
        sortingFn: 'datetime',
        Cell: ({
          row: {
            original: { createdAt },
          },
        }: any) => getShortDateWithTimeString(createdAt),
        /*Cell: ({
          row: {
            original: { createdAt },
          },
        }: any) => (
          <>
            <div style={{ lineHeight: '20px' }}>{getShortDateOnlyWithTimeString(createdAt)}</div>
            <div style={{ lineHeight: '20px', color: darkerGray }}>{getShortTimeOnlyWithTimeString(createdAt)}</div>
          </>
        ),*/
      },
    ],
    [effectiveMaxSize],
  )

  return (
    <Card>
      <NotificationsWrapper />

      <YumHeader>
        <h1>
          {t`Groups `}
          {!teamsLoading && <span style={{ opacity: 0.5, whiteSpace: 'nowrap' }}> ({filteredTeams.length})</span>}
        </h1>
        <Box sx={{ display: 'flex', alignItems: 'center' }} style={{ gap: '8px' }}>
          <YumSmallSelect
            key={currentSelectedGroupId || 'teams'}
            variant="outlined"
            value={currentSelectedGroupId || 'teams'}
            inputProps={{ 'aria-label': t`Group type`, 'data-a11y-label': t`Group type` }}
            onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
              const value = e.target.value as string
              if (value === 'add-category') {
                // Clear notifications before opening the modal
                clearNotification()
                setShowCreateGroupDialog(true)
                // Revert to previous selection after opening dialog
                setSelectedGroupIdForCompany({
                  companyId: userCompany.companyId,
                  groupId: previousSelectedGroupIdRef.current,
                })
              } else {
                // Update the selection
                const newGroupId = value === 'teams' ? null : (value as DivisionCategoryId)
                setSelectedGroupIdForCompany({
                  companyId: userCompany.companyId,
                  groupId: newGroupId,
                })
                // Store this new selection as the previous for next time
                previousSelectedGroupIdRef.current = newGroupId
              }
            }}
          >
            <option value="teams">{t`Teams`}</option>
            {groupsLoading ? (
              <option disabled>{t`Loading categories...`}</option>
            ) : (
              currentCompanyCategories
                ?.slice()
                .sort((a, b) => {
                  const nameA = translateDivisionCategoryType(a, true)
                  const nameB = translateDivisionCategoryType(b, true)
                  return nameA.localeCompare(nameB)
                })
                .map((group: DivisionCategoryRow) => (
                  <option key={group.id} value={group.id}>
                    {group.type === DivisionCategoryType.CUSTOM
                      ? group.customName
                      : translateDivisionCategoryType(group, true)}
                  </option>
                ))
            )}
            <option value="add-category">+ {t`Add group type`}</option>
          </YumSmallSelect>
          <DivisionCategorySettingsWrapper companyId={userCompany.companyId} selectedGroupId={currentSelectedGroupId} />
          {managedCompanies.length > 1 && (
            <YumSmallSelect
              variant="outlined"
              value={userCompany.companyId}
              onChange={handleCompanyChange}
              inputProps={{ 'aria-label': t`Company`, 'data-a11y-label': t`Company` }}
              className="show-on-desktop"
            >
              {managedCompanies.map((userCompany: UserCompanyDto, keyIndex: number) => (
                <option key={keyIndex} value={userCompany.companyId}>
                  {userCompany.companyName}
                </option>
              ))}
            </YumSmallSelect>
          )}
        </Box>
      </YumHeader>
      {managedCompanies.length > 1 && (
        <YumSmallSelect
          variant="outlined"
          value={userCompany.companyId}
          onChange={handleCompanyChange}
          inputProps={{ 'aria-label': t`Company`, 'data-a11y-label': t`Company` }}
          className="hide-on-desktop"
          fullWidth
          //style={{ marginBottom: 16 }}
        >
          {managedCompanies.map((userCompany: UserCompanyDto, keyIndex: number) => (
            <option key={keyIndex} value={userCompany.companyId}>
              {userCompany.companyName}
            </option>
          ))}
        </YumSmallSelect>
      )}

      <div style={{ marginTop: '10px' }}>
        <YumTable
          enableRowNumbers={false}
          enableRowSelection
          columns={columns as any}
          getRowId={(row: any) => row.id as string}
          data={filteredTeams}
          onRowSelectionChange={setRowSelection}
          state={{ rowSelection, isLoading: teamsLoading }}
          initialState={{
            sorting: [
              {
                id: 'name',
                desc: false,
              },
            ],
            density: 'compact',
          }}
          muiTableBodyCellProps={({ row }) => ({
            onClick: (event) => {
              row.toggleExpanded()
            },
            sx: (theme) => ({
              paddingTop: '14px !important',
              paddingBottom: '14px !important',
              lineHeight: '28px !important',
              fontSize: '0.875rem !important',
              color: darkBlue,
            }),
          })}
          muiTableBodyRowProps={({ row }) => ({
            sx: (theme) => ({
              cursor: 'pointer',
              ...(row.getIsExpanded() && {
                backgroundColor: gray,
                MuiTableCell: {
                  padding: '0px',
                },
              }),
              '&.Mui-selected': {
                backgroundColor: 'rgba(0, 111, 255, 0.1)',
                '& > td': {
                  backgroundColor: 'transparent',
                },
              },
            }),
          })}
          renderDetailPanel={({ row }) => (
            <Box
              sx={{
                display: 'block',
                width: '100%',
                maxWidth: '100%',
              }}
            >
              <TeamMembersTableWrapper
                companyId={userCompany.companyId}
                divisionId={row.id}
                categoryId={currentSelectedGroupId || undefined}
                divisions={filteredTeams}
              />
            </Box>
          )}
          /*muiTopToolbarProps={{
            sx: {
              '& > .MuiBox-root': {
                alignItems: 'center',
                padding: '0px',
                '.MuiDialog-scrollPaper &': {
                  padding: '8px',
                },
              },
            }, 
          }}*/
          renderTopToolbarCustomActions={({ table }) => (
            <Box sx={{ display: 'flex', gridGap: '0.5rem', p: '0px' }}>
              {!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected() ? (
                <>
                  <CreateTeamWrapper
                    variant="button"
                    text={currentSelectedGroupId ? t`Create new group` : t`Create new team`}
                    companyId={userCompany.companyId}
                    isUserCreated={false}
                    isAdminView={true}
                    selectedGroupId={currentSelectedGroupId}
                  />

                  {/* Only show the "more" button if at least one menu item is visible and not in a specific group */}
                  {(showGenerateTeamsFeature || showAssignRandomTeamsFeature) && !currentSelectedGroupId && (
                    <>
                      <YumButton
                        variant="outlined"
                        size="small"
                        aria-label={t`More team actions`}
                        title={t`More team actions`}
                        onClick={handleMenuClick}
                        aria-controls={openMenu ? 'team-actions-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={openMenu ? 'true' : undefined}
                      >
                        <MoreIcon style={{ width: '16px', height: '16px' }} />
                      </YumButton>
                      <YumMenu
                        id="team-actions-menu"
                        anchorEl={menuAnchorEl}
                        open={openMenu}
                        onClose={handleMenuClose}
                        items={menuItems}
                        ariaLabelledBy="team-actions-button"
                      />
                    </>
                  )}
                </>
              ) : (
                <>
                  <YumButton onClick={handleLockTeams(table)} variant="outlined" size="small">
                    <LockIcon
                      style={{
                        position: 'relative',
                        top: '-3px',
                        width: '18px',
                        height: '18px',
                        marginRight: '4px',
                      }}
                    />
                    {t`Lock`}
                  </YumButton>
                  <YumButton onClick={handleUnlockTeams(table)} variant="outlined" size="small">
                    <UnlockIcon
                      style={{
                        position: 'relative',
                        top: '-3px',
                        width: '18px',
                        height: '18px',
                        marginRight: '4px',
                      }}
                    />
                    {t`Unlock`}
                  </YumButton>
                  <YumButton onClick={handleDeleteTeams(table)} variant="contained" size="small" color="error">
                    {t`Delete`}
                  </YumButton>
                </>
              )}
            </Box>
          )}
        />
      </div>

      {/* Assign random teams Dialog */}
      <AssignTeamsRandomlyWrapper
        open={showAssignTeamsDialog}
        onClose={closeAssignTeamsDialog}
        companyId={userCompany.companyId}
        companyTeams={filteredTeams}
        companyUsers={companyUsers}
        usersLoading={usersLoading}
        userCompany={userCompany}
      />

      {/* Generate Teams Dialog */}
      <GenerateTeamsWrapper
        open={showGenerateTeamsDialog}
        onClose={closeGenerateTeamsDialog}
        companyId={userCompany.companyId}
      />

      {/* Delete Teams Dialog */}
      <DeleteTeamsWrapper
        open={showDeleteTeamsDialog}
        onClose={closeDeleteTeamsDialog}
        companyId={userCompany.companyId}
        companyTeams={filteredTeams}
        teamsToDelete={teamsToDelete}
        categoryId={currentSelectedGroupId || undefined}
      />

      {/* Rename Team Dialog */}
      <YumDialog open={showRenameDialog}>
        <YumDialogTitle handleClose={closeRenameDialog}>{t`Rename`}</YumDialogTitle>
        <YumDialogContent>
          <YumInput
            startIcon={GroupInactiveIcon}
            autoFocus
            id="teamName"
            label={t`Name`}
            fullWidth
            value={newTeamName}
            onChange={handleTeamNameChange}
            onKeyDown={handleKeyDown}
            style={{ marginBottom: 0 }}
            disabled={renameLoading}
            inputProps={{ autoComplete: 'off' }}
          />
        </YumDialogContent>
        <YumDialogActions>
          <YumCancelButton onClick={closeRenameDialog} disabled={renameLoading} />
          <YumSubmitButton
            text={t`Save`}
            onClick={submitRenameTeam}
            disabled={!newTeamName.trim() || renameLoading || newTeamName.trim() === teamToRename?.name}
            submitInProgress={renameLoading}
            immersiveLoader={true}
          />
        </YumDialogActions>
      </YumDialog>

      {/* Group Division Dialog */}
      <YumDialog open={showCreateGroupDialog}>
        <YumDialogTitle handleClose={() => setShowCreateGroupDialog(false)}>{t`Add group type`}</YumDialogTitle>
        <YumDialogContent>
          <DivisionCategoryTypeSelect
            value={newGroupType}
            onChange={setNewGroupType}
            usedCategoryTypes={usedCategoryTypes}
            style={{ marginBottom: '16px' }}
          />
          <div
            style={{
              fontSize: '0.75rem',
              opacity: 0.5,
              marginTop: '-10px',
              marginBottom: newGroupType === DivisionCategoryType.CUSTOM ? '16px' : 0,
            }}
          >
            {t`Predefined group types are translated`}
            {/* {usedCategoryTypes.size > 0 && (
                <div style={{ marginTop: '4px' }}>
                  {t`Note: Some category types are already in use and cannot be selected again.`}
                </div>
              )} */}
          </div>

          {newGroupType === DivisionCategoryType.CUSTOM && (
            <YumInput
              autoFocus
              id="groupName"
              label={t`Custom name`}
              fullWidth
              value={newGroupName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewGroupName(e.target.value)}
              style={{ marginBottom: 0 }}
              disabled={createGroupLoading}
              inputProps={{ autoComplete: 'off' }}
            />
          )}
        </YumDialogContent>
        <YumDialogActions>
          <YumCancelButton
            onClick={() => {
              setShowCreateGroupDialog(false)
              setNewGroupName('')
              setNewGroupType(DivisionCategoryType.AREA)
            }}
            disabled={createGroupLoading}
          />
          <YumSubmitButton
            text={t`Create`}
            onClick={() => {
              // Prevent submission if a used type is selected (should not happen due to validation)
              if (usedCategoryTypes.has(newGroupType) && newGroupType !== DivisionCategoryType.CUSTOM) {
                return
              }
              if (newGroupType === DivisionCategoryType.CUSTOM && !newGroupName.trim()) return
              setCreateGroupLoading(true)
              createDivisionCategory({
                companyId: userCompany.companyId,
                type: newGroupType,
                customName: newGroupType === DivisionCategoryType.CUSTOM ? newGroupName.trim() : undefined,
              })
              setShowCreateGroupDialog(false)
              setNewGroupName('')
              setNewGroupType(DivisionCategoryType.AREA)
              setCreateGroupLoading(false)
            }}
            disabled={
              (newGroupType === DivisionCategoryType.CUSTOM && !newGroupName.trim()) ||
              createGroupLoading ||
              (usedCategoryTypes.has(newGroupType) && newGroupType !== DivisionCategoryType.CUSTOM)
            }
            submitInProgress={createGroupLoading}
            immersiveLoader={true}
          />
        </YumDialogActions>
      </YumDialog>
    </Card>
  )
}
